import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Badge,
  Button,
  Empty,
  Spinner,
  Progress,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { MoreHorizontal, GripVertical } from '@/lib/icons';
import {
  guidedUseCaseService,
  type GuidedCanvasSummary,
} from '@/services/guided-use-case-service';
import { blockerHeadline, canvasStatus, coverageBarClass, coveragePercent } from './status';
import { downloadTemplate } from './download-template';
import { BulkImportDialog } from './bulk-import-dialog';

export function KaiyaGuidedUseCasesPage() {
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();
  const [importCanvasKey, setImportCanvasKey] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

  const handleDownloadTemplate = async (canvasKey?: string) => {
    try {
      const filename = await downloadTemplate(canvasKey);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error('Could not build the template');
    }
  };

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data, isLoading } = useQuery({
    queryKey: ['guidedUseCases'],
    queryFn: () => guidedUseCaseService.listCanvases(),
  });

  const serverCanvases: GuidedCanvasSummary[] = data?.data ?? [];
  const canvases: GuidedCanvasSummary[] = pendingOrder
    ? pendingOrder
        .map((key) => serverCanvases.find((c) => c.canvas_key === key))
        .filter((c): c is GuidedCanvasSummary => c !== undefined)
    : serverCanvases;

  const reorderMutation = useMutation({
    mutationFn: (canvasKeys: string[]) => guidedUseCaseService.reorderCanvases(canvasKeys),
    // Reorder returns the reordered list, so seed the cache with it before
    // dropping the optimistic order. Invalidating instead would leave the table
    // reading the stale server order until the refetch lands, which flickers
    // the rows back through where they were.
    onSuccess: (result) => {
      queryClient.setQueryData(['guidedUseCases'], result);
      setPendingOrder(null);
      toast.success('Order saved. Practitioners see the canvases in this order.');
    },
    onError: (error: Error) => {
      setPendingOrder(null);
      toast.error(error.message || 'Could not save the order');
    },
  });

  const handleDrop = (targetKey: string) => {
    const sourceKey = dragKey;
    setDragKey(null);
    setOverKey(null);
    if (!sourceKey || sourceKey === targetKey) return;

    const keys = canvases.map((c) => c.canvas_key);
    const from = keys.indexOf(sourceKey);
    const to = keys.indexOf(targetKey);
    if (from === -1 || to === -1) return;

    keys.splice(to, 0, ...keys.splice(from, 1));
    setPendingOrder(keys);
    reorderMutation.mutate(keys);
  };

  const visibilityMutation = useMutation({
    mutationFn: ({ canvasKey, publish }: { canvasKey: string; publish: boolean }) =>
      publish
        ? guidedUseCaseService.publishCanvas(canvasKey)
        : guidedUseCaseService.unpublishCanvas(canvasKey),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['guidedUseCases'] });
      queryClient.invalidateQueries({ queryKey: ['guidedCanvas'] });
      toast.success(
        result.data.published
          ? `${result.data.title ?? result.data.canvas_key} is now offered to practitioners`
          : `${result.data.title ?? result.data.canvas_key} is no longer offered`
      );
    },
    onError: (error: Error) => toast.error(error.message || 'Could not change visibility'),
  });

  return (
    <>
      <Header
        title="Guided Use Cases"
        size="small"
        description="Prosci's definition of a good answer, for every question each canvas asks. Kaiya grades the practitioner's draft against these."
      />

      <ContentContainer>
        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Canvases</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]"><span className="sr-only">Reorder</span></TableHead>
                    <TableHead>Canvas</TableHead>
                    <TableHead>Elements</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {canvases.map((canvas) => {
                    const status = canvasStatus(canvas);

                    return (
                      <TableRow
                        key={canvas.canvas_key}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (dragKey && overKey !== canvas.canvas_key) setOverKey(canvas.canvas_key);
                        }}
                        onDrop={() => handleDrop(canvas.canvas_key)}
                        className={
                          overKey === canvas.canvas_key && dragKey !== canvas.canvas_key
                            ? 'outline outline-2 outline-offset-[-2px] outline-primary'
                            : undefined
                        }
                      >
                        <TableCell>
                          <button
                            type="button"
                            draggable
                            onDragStart={() => setDragKey(canvas.canvas_key)}
                            onDragEnd={() => {
                              setDragKey(null);
                              setOverKey(null);
                            }}
                            disabled={reorderMutation.isPending}
                            aria-label={`Drag to reorder ${canvas.title ?? canvas.canvas_key}`}
                            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground disabled:opacity-40"
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/kaiya-guided-use-cases/${canvas.canvas_key}`}
                            className="font-semibold hover:underline"
                          >
                            {canvas.title ?? canvas.canvas_key}
                          </Link>
                          <div className="text-xs text-muted-foreground mt-1 font-mono">
                            {canvas.canvas_key}
                          </div>
                        </TableCell>
                        <TableCell>{canvas.element_count}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress
                              value={coveragePercent(canvas)}
                              aria-label={`${canvas.coverage_label} for ${canvas.title ?? canvas.canvas_key}`}
                              className={`w-[132px] ${coverageBarClass(canvas)}`}
                            />
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {canvas.authored_count}/{canvas.element_count}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant} tint>{status.label}</Badge>
                          {!canvas.published && !canvas.publishable && (
                            <div className="text-xs text-muted-foreground mt-1.5 max-w-[210px] whitespace-normal">
                              Cannot be offered yet. {blockerHeadline(canvas.blocking_elements)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Actions for ${canvas.title ?? canvas.canvas_key}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => handleDownloadTemplate(canvas.canvas_key)}>
                                Download template
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => setImportCanvasKey(canvas.canvas_key)}>
                                Import definitions
                              </DropdownMenuItem>
                              {canvas.published ? (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    visibilityMutation.mutate({ canvasKey: canvas.canvas_key, publish: false })
                                  }
                                >
                                  Unpublish
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  disabled={!canvas.publishable}
                                  onSelect={() =>
                                    visibilityMutation.mutate({ canvasKey: canvas.canvas_key, publish: true })
                                  }
                                >
                                  Publish
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </ContentContainer>

      <BulkImportDialog
        canvasKey={importCanvasKey}
        open={importCanvasKey !== null}
        onOpenChange={(open) => !open && setImportCanvasKey(null)}
      />
    </>
  );
}
